import { useState, useEffect } from "react";
import { useAuditLogEntries, useAuditLog } from "@/hooks/useAuditLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { Download, Shield, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { toLocalYMD } from "@/lib/validation";

const ACTIONS = [
  { value: "", label: "All Actions" },
  { value: "login", label: "Login" },
  { value: "signup", label: "Signup" },
  { value: "logout", label: "Logout" },
  { value: "settings_updated", label: "Settings Updated" },
  { value: "lead_created", label: "Lead Created" },
  { value: "lead_updated", label: "Lead Updated" },
  { value: "lead_assigned", label: "Lead Assigned" },
  { value: "lead_unassigned", label: "Lead Unassigned" },
  { value: "import", label: "Import" },
  { value: "export", label: "Export" },
  { value: "password_reset", label: "Password Reset" },
  { value: "audit_export", label: "Audit Export" },
];

const PAGE_SIZE = 25;

export default function AuditLog() {
  const [action, setAction] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const { logAction } = useAuditLog();

  const filters = {
    action: action || undefined,
    startDate: startDate ? toLocalYMD(startDate) : undefined,
    endDate: endDate ? toLocalYMD(endDate) : undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const { entries, total, loading, fetchEntries } = useAuditLogEntries(filters);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    setPage(0);
  }, [action, startDate, endDate, search]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  async function handleExportCSV() {
    let query = supabase
      .from("audit_log")
      .select("*")
      .order("created_at", { ascending: false });
    if (action) query = query.eq("action", action);
    if (startDate) query = query.gte("created_at", startDate.toISOString());
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      query = query.lte("created_at", end.toISOString());
    }

    const { data, error } = await query;
    if (error || !data?.length) {
      toast.error(error?.message || "No data to export");
      return;
    }

    const headers = ["Timestamp", "Action", "Resource Type", "Resource ID", "Metadata"];
    const csvRows = data.map((r: any) => [
      r.created_at ? format(new Date(r.created_at), "yyyy-MM-dd HH:mm:ss") : "",
      r.action,
      r.resource_type || "",
      r.resource_id || "",
      JSON.stringify(r.metadata || {}),
    ]);
    const csv = [headers.join(","), ...csvRows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_log_${format(new Date(), "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success(`Exported ${data.length} audit entries`);
    logAction("audit_export", "audit_log", undefined, { count: data.length, filters: { action, startDate, endDate } });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Audit Log</h1>
            <p className="text-sm text-muted-foreground">Track all account activity for compliance</p>
          </div>
        </div>
        <Button onClick={handleExportCSV} variant="outline">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Action</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger><SelectValue placeholder="All Actions" /></SelectTrigger>
                <SelectContent>
                  {ACTIONS.map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <DatePickerField value={startDate} onChange={setStartDate} placeholder="Any" disabled={d => !!endDate && d > endDate} />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <DatePickerField value={endDate} onChange={setEndDate} placeholder="Any" disabled={d => !!startDate && d < startDate} />
            </div>
            <div className="space-y-1.5">
              <Label>Search</Label>
              <Input placeholder="Search actions…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No audit log entries found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {format(new Date(entry.created_at), "MMM d, yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">
                        {entry.action}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {entry.resource_type || "—"}
                      {entry.resource_id && <span className="text-xs text-muted-foreground ml-1">({entry.resource_id.slice(0, 8)}…)</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {Object.keys(entry.metadata).length > 0 ? JSON.stringify(entry.metadata) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
